import { createAsyncThunk } from "@reduxjs/toolkit";
import { customFetch } from "../lib/network/Axios";
import { handleError } from "../lib/ErrorHandler";
import config from "../config";
import { HttpStatusCode } from "axios";
import {
	User,
	UserCreateRequest,
	UserUpdateRequest,
	UserFilter,
	UserListResponse
} from "../types/user.type";

const api = config.apiBaseUrl;

/**
 * Get user by authentication ID
 */
export const getUserByAuthId = createAsyncThunk(
	"user/getByAuthId",
	async (authId: string) => {
		try {
			const response = await customFetch.get(`${api}/auth/${authId}`, {
				headers: {
					"Cache-Control": "no-cache",
					Pragma: "no-cache",
				},
				params: {
					_t: Date.now(),
				},
			});
			if (response?.status >= HttpStatusCode.Ok && response?.status < 300) {
				return response.data;
			}
		} catch (error) {
			handleError(error);
			throw new Error((error as Error).message);
		}
	}
);

/**
 * Get all users with optional filtering
 */
export const getAllUsers = createAsyncThunk(
	"user/getAll",
	async (filter: UserFilter = {}) => {
		try {
			const queryParams = new URLSearchParams();

			// Add search/filter parameters
			if (filter.search) queryParams.append('search', filter.search);
			if (filter.name) queryParams.append('name', filter.name);
			if (filter.email) queryParams.append('email', filter.email);
			if (filter.role) queryParams.append('role', filter.role);
			if (filter.active !== undefined) queryParams.append('active', filter.active.toString());

			// Add pagination parameters
			if (filter.page) queryParams.append('page', filter.page.toString());
			if (filter.pageSize) queryParams.append('pageSize', filter.pageSize.toString());

			// Add sorting parameters
			if (filter.sortBy) queryParams.append('sortBy', filter.sortBy);
			if (filter.sortOrder) queryParams.append('sortOrder', filter.sortOrder);

			const queryString = queryParams.toString();
			const url = `${api}/user${queryString ? `?${queryString}` : ''}`;

			console.log('Fetching users from:', url);

			const response = await customFetch.get(url);
			if (response?.status === HttpStatusCode.Ok) {
				// API returns an array of users directly, transform to match our expected format
				const users = Array.isArray(response.data) ? response.data : [];

				// Ensure required fields exist on each user
				const validUsers = users.map((user: any) => ({
					id: user.id || 0,
					email: user.email || '',
					name: user.name || '',
					profileImageURL: user.profileImageURL || null,
					contactNumber: user.contactNumber || '',
					role: user.role || 'UNKNOWN',
					language: user.language || '',
					lastAccess: user.lastAccess || new Date().toISOString(),
					active: user.active || false,
					...user
				}));

				console.log(`Found ${validUsers.length} users`);

				return {
					users: validUsers,
					totalCount: validUsers.length
				} as UserListResponse;
			}
		} catch (error) {
			handleError(error);
			throw new Error((error as Error).message);
		}
	}
);

/**
 * Get a user by ID
 */
export const getUserById = createAsyncThunk(
	"user/getById",
	async (userId: number) => {
		try {
			const response = await customFetch.get(`${api}/user/${userId}`);
			if (response?.status === HttpStatusCode.Ok) {
				// Ensure we handle the user data correctly
				const userData = response.data;

				// Make sure required fields are present
				const user = {
					id: userData.id || 0,
					email: userData.email || '',
					name: userData.name || '',
					profileImageURL: userData.profileImageURL || null,
					contactNumber: userData.contactNumber || '',
					role: userData.role || 'UNKNOWN',
					language: userData.language || '',
					lastAccess: userData.lastAccess || new Date().toISOString(),
					active: userData.active || false,
					...userData
				};

				console.log('Retrieved user details:', user.id, user.name);
				return user as User;
			}
		} catch (error) {
			handleError(error);
			throw new Error((error as Error).message);
		}
	}
);

/**
 * Create a new user
 */
export const createUser = createAsyncThunk(
	"user/create",
	async (user: UserCreateRequest) => {
		try {
			// Transform to match API expected format
			const apiPayload = {
				email: user.email,
				name: user.name,
				contactNumber: user.contactNumber || '',
				role: user.role,
				language: user.language || 'en',
				active: user.active ?? true,
				// Removed languageCode as it's not supported by the API
				paswordHash: user.password, // API expects paswordHash instead of password
			};

			console.log('Creating user with payload:', apiPayload);

			const response = await customFetch.post(`${api}/user`, apiPayload);
			if (response?.status === HttpStatusCode.Created || response?.status === HttpStatusCode.Ok) {
				return response.data as User;
			}
		} catch (error) {
			console.error('Error creating user:', error);
			handleError(error);
			throw new Error((error as Error).message);
		}
	}
);

/**
 * Update an existing user
 */
export const updateUser = createAsyncThunk(
	"user/update",
	async (user: UserUpdateRequest) => {
		try {
			// Transform to match API expected format, removing unsupported fields
			const apiPayload = {
				email: user.email,
				name: user.name,
				contactNumber: user.contactNumber || '',
				role: user.role,
				language: user.language || 'en',
				active: user.active,
				// Removing languageCode as it's not supported by the API
			};

			console.log('Updating user with payload:', apiPayload);

			// Using PATCH for partial updates instead of PUT
			const response = await customFetch.patch(`${api}/user/${user.id}`, apiPayload);
			if (response?.status === HttpStatusCode.Ok) {
				return response.data as User;
			}
		} catch (error) {
			console.error('Error updating user:', error);
			handleError(error);
			throw new Error((error as Error).message);
		}
	}
);

/**
 * Delete a user
 */
export const deleteUser = createAsyncThunk(
	"user/delete",
	async (userId: number) => {
		try {
			const response = await customFetch.delete(`${api}/user/${userId}`);
			if (response?.status === HttpStatusCode.Ok || response?.status === HttpStatusCode.NoContent) {
				return userId;
			}
		} catch (error) {
			handleError(error);
			throw new Error((error as Error).message);
		}
	}
);
